import { TreeItemData } from '@aibees/elephant/components/checkbox-tree-view';
import { SearchFilter } from '@aibees/elephant/feature/search-filters';
import { MetadataItem } from '@aibees/elephant/hooks';
import { TreeViewBaseItem } from '@mui/x-tree-view';
import { useMemo } from 'react';

function buildMetadataMap(
  metadataApiData?: MetadataItem[]
): Map<string, number> | undefined {
  if (!metadataApiData) return undefined;
  const map = new Map<string, number>();
  for (const m of metadataApiData) {
    map.set(m.value.toLowerCase(), m.count ?? 0);
  }
  return map;
}

/**
 * Tree-only: builds items from treeItems (treeOptions). No flat/API path.
 */
export function useGetTreeItems(
  _config: SearchFilter['config'],
  metadata: SearchFilter['metadata'],
  _utils: SearchFilter['utils'],
  _icon: SearchFilter['icon'],
  treeItems: TreeViewBaseItem<TreeItemData>[] | undefined,
  _apiData: undefined,
  metadataApiData?: MetadataItem[],
  _tabValue?: number,
  selectedValues?: string[],
  multiple?: boolean
) {
  return useMemo(() => {
    const customOptions = !!metadata?.autocomplete?.customOptions;
    const metadataMap = buildMetadataMap(metadataApiData);
    const selectedSet = selectedValues ? new Set(selectedValues) : undefined;

    if (!customOptions || !treeItems?.length) {
      return { items: [] };
    }

    return {
      items:
        treeItems
          ?.filter(
            (o) => !!multiple || (!multiple && !selectedSet?.has(o.value))
          )
          .map((item) => {
            if (item.children?.length) {
              return {
                ...item,
                metadataCount: item.children.reduce(
                  (acc, child) =>
                    acc + (metadataMap?.get(child.value.toLowerCase()) ?? 0),
                  0
                ),
                children: item.children.map((child) => ({
                  ...child,
                  metadataCount: metadataMap?.get(child.value.toLowerCase()),
                })),
              };
            } else {
              return {
                ...item,
                metadataCount: metadataMap?.get(item.value.toLowerCase()),
              };
            }
          }) ?? [],
    };
  }, [
    metadata?.autocomplete?.customOptions,
    metadataApiData,
    selectedValues,
    multiple,
    treeItems,
  ]);
}
